/**
 * Service API pour les tickets de support - Utilisateurs
 * Routes accessibles à tous les utilisateurs authentifiés
 */

import type {
  Ticket,
  CreateTicketDto,
  PaginatedTickets,
  TicketMessage,
} from '../types/tickets.types';
import { fetchWithAuth, safeJsonParse } from '@/utils/api';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001/api/v1';
const API_BASE = `${API_URL}/support/tickets`;

async function handleResponse<T>(response: Response): Promise<T> {
  if (!response.ok) {
    const error = await safeJsonParse(response).catch(() => ({ message: 'Erreur serveur' }));
    throw new Error(error.message || (typeof error.error === 'object' ? error.error?.message : error.error) || `HTTP ${response.status}`);
  }
  const json = await safeJsonParse(response);
  return json.data;
}

export const ticketsAPI = {
  /**
   * Liste de mes tickets avec pagination
   */
  async list(
    options: { page?: number; limit?: number; status?: string } = {}
  ): Promise<PaginatedTickets> {
    const params = new URLSearchParams();

    if (options.page) params.set('page', String(options.page));
    if (options.limit) params.set('limit', String(options.limit));
    if (options.status) params.set('status', options.status);

    const response = await fetchWithAuth(`${API_BASE}?${params.toString()}`);

    const json = await safeJsonParse(response);
    if (!response.ok) {
      throw new Error(json.message || 'Erreur lors du chargement des tickets');
    }

    return {
      tickets: json.data,
      pagination: json.meta,
    };
  },

  /**
   * Récupérer un ticket par ID
   */
  async getById(id: string): Promise<Ticket> {
    const response = await fetchWithAuth(`${API_BASE}/${id}`);
    return handleResponse<Ticket>(response);
  },

  /**
   * Créer un nouveau ticket
   */
  async create(data: CreateTicketDto): Promise<Ticket> {
    const response = await fetchWithAuth(API_BASE, {
      method: 'POST',
      body: JSON.stringify(data),
    });
    return handleResponse<Ticket>(response);
  },

  /**
   * Ajouter un message à un ticket
   */
  async addMessage(ticketId: string, content: string, isInternal?: boolean): Promise<TicketMessage> {
    const response = await fetchWithAuth(`${API_BASE}/${ticketId}/messages`, {
      method: 'POST',
      body: JSON.stringify({ content, isInternal }),
    });
    return handleResponse<TicketMessage>(response);
  },

  /**
   * Mettre à jour un ticket
   * GHOST ENDPOINT: Backend route PATCH /support/tickets/:id does not exist yet
   */
  async update(_id: string, _data: any): Promise<Ticket> {
    throw new Error('Backend endpoint PATCH /support/tickets/:id not implemented');
  },

  /**
   * Assigner un ticket
   * GHOST ENDPOINT: Backend route POST /support/tickets/:id/assign does not exist yet
   */
  async assign(_id: string, _assignedToId: string): Promise<Ticket> {
    throw new Error('Backend endpoint POST /support/tickets/:id/assign not implemented');
  },

  /**
   * Désassigner un ticket
   * GHOST ENDPOINT: Backend route POST /support/tickets/:id/unassign does not exist yet
   */
  async unassign(_id: string): Promise<Ticket> {
    throw new Error('Backend endpoint POST /support/tickets/:id/unassign not implemented');
  },

  /**
   * Résoudre un ticket
   * GHOST ENDPOINT: Backend route POST /support/tickets/:id/resolve does not exist yet
   */
  async resolve(_id: string): Promise<Ticket> {
    throw new Error('Backend endpoint POST /support/tickets/:id/resolve not implemented');
  },

  /**
   * Fermer un ticket
   */
  async close(id: string): Promise<Ticket> {
    const response = await fetchWithAuth(`${API_BASE}/${id}/close`, {
      method: 'POST',
    });
    return handleResponse<Ticket>(response);
  },

  /**
   * Rouvrir un ticket
   */
  async reopen(id: string): Promise<Ticket> {
    const response = await fetchWithAuth(`${API_BASE}/${id}/reopen`, {
      method: 'POST',
    });
    return handleResponse<Ticket>(response);
  },

  /**
   * Récupérer les statistiques des tickets
   * GHOST ENDPOINT: Backend route GET /support/tickets/stats does not exist yet
   */
  async getStats(_filters?: { dateFrom?: string; dateTo?: string }): Promise<Record<string, unknown>> {
    // Backend endpoint not implemented - return empty object
    return {};
  },

  /**
   * Récupérer les métriques SLA
   * GHOST ENDPOINT: Backend route GET /support/tickets/sla-metrics does not exist yet
   */
  async getSLAMetrics(_filters?: { dateFrom?: string; dateTo?: string }): Promise<Record<string, unknown>> {
    // Backend endpoint not implemented - return empty object
    return {};
  },

  /**
   * Récupérer les tickets non assignés
   * GHOST ENDPOINT: Backend route GET /support/tickets/unassigned does not exist yet
   */
  async getUnassigned(_limit: number = 10): Promise<Ticket[]> {
    // Backend endpoint not implemented - return empty array
    return [];
  },
};
